/**
 * @file template-editor-extensions.ts
 * @description TipTap-расширения визуального редактора шаблонов документов
 * (phase19.7):
 *  - PlaceholderChip — инлайн-атом `<span data-ph="path">` (чип поля);
 *  - ItemsTableRow — TableRow c атрибутом data-items-row (строка
 *    «Таблицы позиций», сериализуется в {{#each items}});
 *  - Indent — отступ абзаца/заголовка (кратно 24px, 0..6).
 */

import {
  Extension,
  Node,
  mergeAttributes,
  type CommandProps,
} from "@tiptap/core";
import { TableRow } from "@tiptap/extension-table";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tplIndent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

/** Чип-плейсхолдер Handlebars-поля. Не редактируется, удаляется как атом. */
export const PlaceholderChip = Node.create({
  name: "placeholderChip",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      path: {
        default: "",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-ph") ?? "",
        renderHTML: (attrs: Record<string, unknown>) => ({
          "data-ph": String(attrs.path ?? ""),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-ph]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: "tpl-ph-chip" }),
      String(node.attrs.path),
    ];
  },
});

/** TableRow с сохранением data-items-row (иначе TipTap отбрасывает атрибут). */
export const ItemsTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      itemsRow: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("data-items-row"),
        renderHTML: (attrs: Record<string, unknown>) =>
          attrs.itemsRow ? { "data-items-row": String(attrs.itemsRow) } : {},
      },
    };
  },
});

const INDENT_STEP_PX = 24;
const INDENT_MAX = 6;
const INDENT_TYPES = ["paragraph", "heading"];

/** Отступ абзаца (margin-left кратно 24px) — кнопки ⇤/⇥ в тулбаре. */
export const Indent = Extension.create({
  name: "tplIndent",

  addGlobalAttributes() {
    return [
      {
        types: INDENT_TYPES,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el: HTMLElement) => {
              const ml = Number.parseInt(el.style.marginLeft || "0", 10);
              return Number.isNaN(ml) ? 0 : Math.round(ml / INDENT_STEP_PX);
            },
            renderHTML: (attrs: Record<string, unknown>) => {
              const indent = Number(attrs.indent ?? 0);
              return indent > 0
                ? { style: `margin-left: ${indent * INDENT_STEP_PX}px` }
                : {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    const apply =
      (delta: number) =>
      () =>
      ({ tr, state, dispatch }: CommandProps): boolean => {
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!INDENT_TYPES.includes(node.type.name)) return;
          const current = Number(node.attrs.indent ?? 0);
          const next = Math.min(INDENT_MAX, Math.max(0, current + delta));
          if (next !== current) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      };
    return {
      indent: apply(1),
      outdent: apply(-1),
    };
  },
});
